import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Search, Loader2, ShieldCheck, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SEOHead } from '@/components/SEOHead';
import { useAdmin } from '@/hooks/useAdmin';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { AdminNotificationBell } from '@/components/admin/AdminNotificationBell';
import logo from '@/assets/gaius-logo.webp';

interface EmailLog {
  id: string;
  recipient_email: string;
  recipient_name: string | null;
  email_type: string;
  subject: string;
  status: string;
  created_at: string;
}

const typeLabel: Record<string, string> = {
  welcome: 'Bienvenue',
  enrollment_confirmation: 'Confirmation inscription',
  waitlist_confirmation: 'Confirmation waitlist',
  reminder: 'Rappel',
  reschedule: 'Replanification',
  other: 'Autre',
};

const statusBadge = (s: string) => {
  switch (s) {
    case 'delivered': return <Badge className="bg-emerald-100 text-emerald-800 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Délivré</Badge>;
    case 'opened': return <Badge className="bg-blue-100 text-blue-800 text-xs"><Eye className="w-3 h-3 mr-1" />Ouvert</Badge>;
    case 'failed': return <Badge className="bg-red-100 text-red-800 text-xs"><XCircle className="w-3 h-3 mr-1" />Échoué</Badge>;
    default: return <Badge className="bg-yellow-100 text-yellow-800 text-xs"><Clock className="w-3 h-3 mr-1" />Envoyé</Badge>;
  }
};

export const AdminEmailLogsPage = () => {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    if (!adminLoading && !authLoading && (!user || !isAdmin)) navigate('/');
  }, [isAdmin, adminLoading, authLoading, user, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchLogs = async () => {
      const { data } = await supabase
        .from('email_logs' as any)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (data) setLogs(data as any[]);
      setLoading(false);
    };
    fetchLogs();
  }, [isAdmin]);

  const filtered = useMemo(() => {
    let result = logs;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(l => l.recipient_email.toLowerCase().includes(s) || l.subject.toLowerCase().includes(s) || (l.recipient_name || '').toLowerCase().includes(s));
    }
    if (filterType !== 'all') {
      result = result.filter(l => l.email_type === filterType);
    }
    return result;
  }, [logs, search, filterType]);

  const stats = useMemo(() => ({
    total: logs.length,
    sent: logs.filter(l => l.status === 'sent').length,
    delivered: logs.filter(l => l.status === 'delivered').length,
    opened: logs.filter(l => l.status === 'opened').length,
    failed: logs.filter(l => l.status === 'failed').length,
  }), [logs]);

  if (adminLoading || authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="Suivi Emails — Admin Gaius" description="Historique des emails envoyés" />

      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/"><img src={logo} alt="Gaius" className="h-10" /></Link>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <div>
                  <h1 className="text-xl font-bold text-foreground">Suivi Emails</h1>
                  <p className="text-sm text-muted-foreground">Historique des emails envoyés</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AdminNotificationBell />
              <Button variant="outline" size="sm" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Tableau de bord
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="pt-4 text-center"><div className="text-2xl font-bold">{stats.total}</div><p className="text-xs text-muted-foreground">Total</p></CardContent></Card>
          <Card><CardContent className="pt-4 text-center"><div className="text-2xl font-bold">{stats.sent}</div><p className="text-xs text-muted-foreground">Envoyés</p></CardContent></Card>
          <Card><CardContent className="pt-4 text-center"><div className="text-2xl font-bold">{stats.delivered}</div><p className="text-xs text-muted-foreground">Délivrés</p></CardContent></Card>
          <Card><CardContent className="pt-4 text-center"><div className="text-2xl font-bold">{stats.opened}</div><p className="text-xs text-muted-foreground">Ouverts</p></CardContent></Card>
          <Card><CardContent className="pt-4 text-center"><div className="text-2xl font-bold text-destructive">{stats.failed}</div><p className="text-xs text-muted-foreground">Échoués</p></CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Rechercher par email ou sujet..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-[220px]"><SelectValue placeholder="Type d'email" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              {Object.entries(typeLabel).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Destinataire</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Sujet</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Aucun email trouvé</TableCell>
                  </TableRow>
                ) : (
                  filtered.map(l => (
                    <TableRow key={l.id}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(l.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="text-sm font-medium">{l.recipient_name || '—'}</span>
                          <span className="text-xs text-muted-foreground block">{l.recipient_email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{typeLabel[l.email_type] || l.email_type}</Badge>
                      </TableCell>
                      <TableCell className="text-sm max-w-[250px] truncate">{l.subject}</TableCell>
                      <TableCell>{statusBadge(l.status)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
